import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormNode } from '@/utils/api/aboboApiTypes';
import BaseQuestionType from './BaseQuestionType';

// Import shadcn components
import { Input } from '@/components/ui/input';
import QuestionHeading from '@/components/QuestionHeading';
import QuestionHelperText from '@/components/QuestionHelperText';

interface PhoneProps {
  node: FormNode;
}

export default forwardRef<any, PhoneProps>(function Phone(
  { node },
  ref
) {
  const { t } = useTranslation();

  const validatePhone = (values: any[]) => {
    const value = values?.[0]?.data || '';
    
    if (node.metadata?.required && !value) {
      return t('questionTypes.phone.validation.required');
    }
    
    if (value) {
      // Basic phone validation - you can make this more sophisticated
      const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
      const cleanPhone = value.replace(/\D/g, '');
      
      if (!phoneRegex.test(cleanPhone)) {
        return t('questionTypes.phone.validation.invalid');
      }
      
      if (cleanPhone.length < 10) {
        return t('questionTypes.phone.validation.tooShort');
      }
    }
    
    return true;
  };

  const transformInput = (values: any[]) => {
    return values?.[0]?.data || '';
  };

  const transformOutput = (value: string) => {
    return value ? [{ data: value }] : [];
  };

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return value;
  };

  return (
    <BaseQuestionType
      node={node}
      validate={validatePhone}
      transform={{
        input: transformInput,
        output: transformOutput,
      }}
    >
      {(field) => (
        <div className="space-y-4" ref={ref}>
          <QuestionHeading required={node.metadata?.required}>
            {node.body}
          </QuestionHeading>

          {node.helperText && (
            <QuestionHelperText>
              {node.helperText}
            </QuestionHelperText>
          )}

          <Input
            {...field}
            autoComplete="off"
            type="tel"
            placeholder="(555) 123-4567"
            onChange={(e) => {
              const formatted = formatPhoneNumber(e.target.value);
              field.onChange(formatted);
            }}
            className="w-full"
          />

          {field.error && (
            <p className="text-red-500 text-sm">{field.error}</p>
          )}
        </div>
      )}
    </BaseQuestionType>
  );
});
